import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { User, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { TitanLogo } from './TitanLogo';

const Nav = styled.nav`
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 80px;
  display: flex;
  align-items: center;
  box-shadow: var(--shadow-sm);
  transition: background-color 0.3s ease, border-color 0.3s ease;
`;

const NavInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: ${theme.fontWeight.bold};
  color: var(--color-primary);
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  font-family: 'Inter', 'Montserrat', sans-serif;
  transition: color 0.3s ease;
  &:hover {
    color: var(--color-primary-dark);
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.sm};
  }
`;

const NavLink = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  color: ${({ active }) => active ? 'var(--color-primary)' : 'var(--color-text-muted)'};
  font-size: ${theme.fontSize.base};
  font-weight: ${({ active }) => active ? theme.fontWeight.semibold : theme.fontWeight.normal};
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.3s ease;
  &:hover {
    color: var(--color-primary);
    background: var(--color-surface-hover);
  }
  
  .nav-text {
    @media (max-width: ${theme.breakpoints.tablet}) {
      display: none;
    }
  }
`;

const ThemeToggle = styled.button`
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  &:hover {
    background: var(--color-surface-hover);
    color: var(--color-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useTheme();

  const isActive = (path: string) => location.pathname === path;

  return (
    <Nav>
      <NavInner>
        <Logo onClick={() => navigate('/')}>
          <TitanLogo size={40} showText={true} />
        </Logo>
        <NavLinks>
          <ThemeToggle 
            onClick={toggleTheme}
            aria-label={mode === 'dark' ? 'Alternar para tema claro' : 'Alternar para tema escuro'}
            title={mode === 'dark' ? 'Tema claro' : 'Tema escuro'}
          >
            {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </ThemeToggle>
          <NavLink active={isActive('/profile')} onClick={() => navigate('/profile')}>
            <User size={18} style={{ display: 'inline', marginRight: theme.spacing.xs }} />
            <span className="nav-text">Configurações</span>
          </NavLink>
        </NavLinks>
      </NavInner>
    </Nav>
  );
};

export default Navbar;
